import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { RoutePlannerFormComponent } from '../../route-planner-form.component';
import { FormBuilder, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { FormValidators } from 'src/app/@forms/validators/validators';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  Subscription,
} from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import {
  eDepartureTimeOptions,
  DEPARTURE_TIME_OPTIONS,
} from 'src/app/modules/route-planner/constants/departure-time-options.constants';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { City } from 'src/app/modules/route-planner/interfaces/location/city.interface';
import { LinesBetweenCitiesForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/lines-between-cities-form';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { LinesBetweenCitiesResultsContainerComponent } from 'src/app/modules/route-planner/containers/route-planning/route-planning-results-container/lines-between-cities-results-container/lines-between-cities-results-container.component';
import { DefaultMapComponent } from 'src/app/modules/route-planner/components/shared/map/default-map.component';
import { IntercityLines } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/intercity-lines.interface';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  FIND_LINES,
  FROM_CITY,
  SWITCH_LOCATIONS,
  TO_CITY,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { PlannerSurveyOrchestratorService } from 'src/app/modules/route-planner/services/survey/planner-survey-orchestrator.service';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';

@Component({
    selector: 'app-lines-between-cities-form',
    templateUrl: './lines-between-cities-form.component.html',
    styleUrls: ['./lines-between-cities-form.component.scss'],
    standalone: false
})
export class LinesBetweenCitiesFormComponent
  extends RoutePlannerFormComponent
  implements OnInit {

  private readonly initialLinesBetweenCitiesFormValues = {
    origin: null,
    destination: null,
    timeOpt: eDepartureTimeOptions.NOW,
    arrival: {
      date: new Date(),
      time: this.utilsService.getCurrentTime(),
    },
    departure: {
      date: new Date(),
      time: this.utilsService.getCurrentTime(),
    },
  };


  @Output() toggleSearchButton: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() toogleLoader: EventEmitter<boolean> = new EventEmitter<boolean>();

  departureTimeOptions: Observable<SelectOption<eDepartureTimeOptions>[]> = of(
    DEPARTURE_TIME_OPTIONS
  );

  originCities$: Observable<SelectOption<City>[]> = of([]);
  destinationCities$: Observable<SelectOption<City>[]> = of([]);

  fromCityText: string;
  toCityText: string;
  switchLocationsText: string = SWITCH_LOCATIONS.hebrew;


  // Flag to track manual tab selection.
  private isManualTabSelection: boolean = false;
  // Flag to prevent duplicate calls.
  private isSubmitting: boolean = false;
  private lastSubmitParams: string = '';
  subscription: Subscription;
  isInitializing = true;

  constructor(
    fb: FormBuilder,
    locationsService: LocationService,
    queryParamsService: QueryParamsService,
    routePlanningService: RoutePlanningService,
    router: Router,
    private readonly utilsService: UtilsService,
    private readonly languageService: LanguageService,
    private readonly scheduleService: ScheduleService,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly plannerSurveyOrchestrator: PlannerSurveyOrchestratorService
  ) {
    super(
      eTab.INTER_CITY_LINE,
      'מצא קווים',
      fb,
      locationsService,
      queryParamsService,
      routePlanningService,
      router,
      utilsService
    );

    this.routePlannerForm = fb.group({
      ...this.routePlannerForm.controls,
      origin: new FormControl<string>(this.initialLinesBetweenCitiesFormValues.origin, [
        FormValidators.required,
        FormValidators.validateNotString(),
      ]),
      destination: new FormControl<string>(this.initialLinesBetweenCitiesFormValues.destination, [
        FormValidators.required,
        FormValidators.validateNotString(),
      ]),
      timeOpt: new FormControl<eDepartureTimeOptions>(
        eDepartureTimeOptions.NOW
      ),
      arrival: fb.group({
        date: new FormControl<Date>(this.initialLinesBetweenCitiesFormValues.arrival.date),
        time: new FormControl<string>(this.initialLinesBetweenCitiesFormValues.arrival.time, [FormValidators.sourceDestiationTimeValidator(() => this.routePlannerForm?.get('timeOpt')?.value,
          eDepartureTimeOptions.ARRIVAL, () => this.routePlannerForm?.get('arrival.date'))]),
      }),
      departure: fb.group({
        date: new FormControl<Date>(this.initialLinesBetweenCitiesFormValues.departure.date),
        time: new FormControl<string>(this.initialLinesBetweenCitiesFormValues.departure.time, [FormValidators.sourceDestiationTimeValidator(() => this.routePlannerForm?.get('timeOpt')?.value,
          eDepartureTimeOptions.DEPART, () => this.routePlannerForm?.get('departure.date'))]),
      }),
    });

    // Listen for tab changes to flag manual selection.
    this.queryParamsService._tabChanged$.subscribe((tab) => {
      if (tab == this.formId) {
        this.isManualTabSelection = true;
      }
    });
  }

  ngOnInit(): void {
    this.initializeAutocomplete();
    this.initializeMultiLanguageTexts();
    // We'll let autofillQueryParams handle the query parameters
    // Don't do any query parameter processing here

    // Reset the manual tab selection flag.
    this.isManualTabSelection = false;

    const hasTimeParam = !!this.queryParamsService.currentParams['time'];
    if (hasTimeParam) {
      // Allow date-time picker to initialize from URL params first
      setTimeout(() => {
        this.isInitializing = false;
      }, 200);
    } else {
      this.isInitializing = false;
    }

    this.routePlannerForm.controls['timeOpt'].valueChanges.subscribe((value) => {
      if (this.isInitializing) {
        return;
      }

      const departureTime = this.routePlannerForm.get('departure.time');
      const arrivalTime = this.routePlannerForm.get('arrival.time');

      departureTime.setValue(this.utilsService.getCurrentTime())
      arrivalTime.setValue(this.utilsService.getCurrentTime())

    });

    this.routePlannerForm.get('arrival.date')?.valueChanges.subscribe(() => {
      this.routePlannerForm.get('arrival.time')?.updateValueAndValidity();
    });

    this.routePlannerForm.get('departure.date')?.valueChanges.subscribe(() => {
      this.routePlannerForm.get('departure.time')?.updateValueAndValidity();
    });

    this.routePlannerForm.controls['origin'].valueChanges.subscribe(
      (val: City | string | null) => {
        if (val && typeof val === 'object' && val.cityName) {
          this.queryParamsService.setQueryParam('org', val.cityName);
        }
      }
    );
    this.routePlannerForm.controls['destination'].valueChanges.subscribe(
      (val: City | string | null) => {
        if (val && typeof val === 'object' && val.cityName) {
          this.queryParamsService.setQueryParam('dest', val.cityName);
        }
      }
    );
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.fromCityText = FROM_CITY[language.language];
      this.toCityText = TO_CITY[language.language];
      this.switchLocationsText = SWITCH_LOCATIONS[language.language];

      this.buttonTitle = FIND_LINES[language.language];
    });
  }

  /**
   * Search for cities and fill the form with the results
   */
  private searchAndFillCities(
    originLabel: string,
    destLabel: string,
  ): void {
    // Add a flag to prevent duplicate searches
    const submitKey = `${originLabel}-${destLabel}`;
    if (this.isSubmitting && this.lastSubmitParams === submitKey) {
      return;
    }

    // Set the submitting flag regardless of triggerSubmission to prevent duplicate searches
    this.isSubmitting = true;
    this.lastSubmitParams = submitKey;

    let originSubscription: Subscription;
    let destSubscription: Subscription;

    this.toogleLoader.emit(true)
    // First search for origin city
    originSubscription = this.autoCompleteService.searchCities(originLabel).subscribe({
      next: (originResults) => {
        if (!originResults?.length) {
          this.isSubmitting = false;
          originSubscription?.unsubscribe();
          return;
        } else {
          originSubscription?.unsubscribe();
        }

        const originCity = originResults.find(result => result.label === originLabel)?.value;

        // Then search for destination city
        destSubscription = this.autoCompleteService.searchCities(destLabel).subscribe({
          next: (destResults) => {
            if (!destResults?.length) {
              this.isSubmitting = false;
              destSubscription?.unsubscribe();
              return;
            } else {
              destSubscription?.unsubscribe();
            }

            const destCity = destResults.find(result => result.label === destLabel)?.value;

            // Patch the form with the found cities
            this.routePlannerForm.patchValue({
              origin: originCity,
              destination: destCity,
            });

            // Delay slightly to ensure form updates propagate
            setTimeout(() => {
              if (this.routePlannerForm.valid) {
                this.submitRoutePlannerForm();
              } else {
                this.toogleLoader.emit(false)

              }
              this.isSubmitting = false;
              destSubscription?.unsubscribe();
            }, 500);


          },
          error: (error) => {
            console.error('Error searching for destination city:', error);
            this.isSubmitting = false;
            destSubscription?.unsubscribe();
            this.toogleLoader.emit(false)
          },
        });
      },
      error: (error) => {
        console.error('Error searching for origin city:', error);
        this.isSubmitting = false;
        originSubscription?.unsubscribe();
        this.toogleLoader.emit(false)
      },
    });
  }

  override onTabLeave() {
    this.routePlannerForm.reset(this.initialLinesBetweenCitiesFormValues)
  }

  // This is the only method that should handle query parameters
  override autofillQueryParams(): void {
    const qp = this.queryParamsService.currentParams;
    if (!qp) {
      return;
    }

    const originLabel = qp['originCity'] || qp['org'];
    const destLabel = qp['destinationCity'] || qp['dest'];

    if (!originLabel || !destLabel) {
      return;
    }


    // Use our new method to handle the city search
    this.searchAndFillCities(originLabel, destLabel);
  }

  private initializeAutocomplete() {
    const toCitySearchTerm = (value: unknown): string =>
      typeof value === 'string' ? value : '';

    this.originCities$ = this.routePlannerForm.controls[
      'origin'
    ].valueChanges.pipe(
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      distinctUntilChanged(),
      switchMap((value) =>
        this.autoCompleteService.searchCities(toCitySearchTerm(value))
      )
    );

    this.destinationCities$ = this.routePlannerForm.controls[
      'destination'
    ].valueChanges.pipe(
      debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
      distinctUntilChanged(),
      switchMap((value) =>
        this.autoCompleteService.searchCities(toCitySearchTerm(value))
      )
    );
  }

  public override submitRoutePlannerForm() {
    if (!!this.routePlannerForm.valid) {
      const selectedTimeOpt = this.routePlannerForm.controls['timeOpt'].value;
      const routePlanningFormValues: LinesBetweenCitiesForm =
        this.routePlannerForm.value;

      const routesObs$ = this.getRoutePlanningObs(
        routePlanningFormValues,
        selectedTimeOpt
      );

      this.subscription = routesObs$.subscribe({
        next: (routePlanningRes: ServiceResponse<IntercityLines[]>) => {
          this.routePlannerForm.markAsPristine();
          this.toggleSearchButton.emit(false);
          this.toogleLoader.emit(false);

          this.openLinesBetweenCitiesResultsContainer(routePlanningRes, routePlanningFormValues);
          if (routePlanningRes.data?.length) {
            this.plannerSurveyOrchestrator.scheduleSurveyAfterSearch({
              plannerScenario: 'intercity',
              originCityId: routePlanningFormValues.origin?.id,
              destinationCityId: routePlanningFormValues.destination?.id,
            });
          }
          setTimeout(() => {
            this.subscription?.unsubscribe();
          }, 500);
        },
        error: (error) => {
          this.toggleSearchButton.emit(false);
          this.toogleLoader.emit(false);
          setTimeout(() => {
            this.subscription?.unsubscribe();
          }, 500);
        },
        complete: () => {
          this.toggleSearchButton.emit(false);
          this.toogleLoader.emit(false);
          setTimeout(() => {
            this.subscription?.unsubscribe();
          }, 500);
        },
      });
    }
  }

  private openLinesBetweenCitiesResultsContainer(routePlanningRes: ServiceResponse<IntercityLines[]>, routePlanningFormValues: LinesBetweenCitiesForm) {
    this.routePlanningService._formSubject.next({
      formValues: routePlanningFormValues,
      results: routePlanningRes.data,
    });

    // Switch to default map (no address picker) while results are open so we don't allow searches
    this.routePlanningService._mapOverlaySub.next({
      mapComponent: DefaultMapComponent,
      data: { title: '' },
    });

    this.routePlanningService._componentOverlaySub.next({
      componentType: LinesBetweenCitiesResultsContainerComponent,
      data: {
        closeCallbackFn: (reSubmitForm: boolean) => {
          // Restore address autocomplete map when results overlay closes
          this.routePlanningService._mapOverlaySub.next(null);
          if (reSubmitForm) {
            this.openLinesBetweenCitiesResultsContainer(routePlanningRes, routePlanningFormValues);
          }
        },
      },
    });
  }

  private getRoutePlanningObs(
    routePlanningFormValues: LinesBetweenCitiesForm,
    selectedTimeOpt: any
  ) {
    LogService.logInfo(`RoutePlanningForm values`, routePlanningFormValues);
    this.toggleSearchButton.emit(true);
    this.toogleLoader.emit(true);

    return this.scheduleService.getIntercityLines({
      origin: routePlanningFormValues.origin.cityName,
      destination: routePlanningFormValues.destination.cityName,
      arrivalTime: this.routePlanningService
        .getArrival(selectedTimeOpt, routePlanningFormValues)
        ?.toISOString(),
      departureTime: this.routePlanningService
        .getDeparture(selectedTimeOpt, routePlanningFormValues)
        ?.toISOString(),
      isNewSearch: true
    });
  }

  validateCity(
    value: string,
    cities$: Observable<SelectOption<City>[]>
  ): Observable<boolean> {
    return cities$.pipe(
      map((cities) => {
        if (!cities || cities.length === 0) {
          return false; // Invalid if no cities are available
        }
        return cities.some((city) => city.label === value);
      })
    );
  }

  // Helper method to retrieve form validation errors for debugging.
  private getFormValidationErrors(): any {
    const errors: any = {};
    Object.keys(this.routePlannerForm.controls).forEach((key) => {
      const control = this.routePlannerForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }
}
